import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Authentication Service
export const authService = {
  login: (email, password) =>
    apiClient.post('/auth/login', { email, password }),
};

// Case Service
export const caseService = {
  createCase: (caseData, userId, officeId) =>
    apiClient.post(`/cases?userId=${userId}&officeId=${officeId}`, caseData),
  
  getCaseById: (caseId) =>
    apiClient.get(`/cases/${caseId}`),

  getCaseTouchPoints: (caseId) =>
    apiClient.get(`/cases/${caseId}/touch-points`),

  getCaseWorkflow: (caseId) =>
    apiClient.get(`/cases/${caseId}/workflow`),

  startCaseWorkflow: (caseId) =>
    apiClient.post(`/cases/${caseId}/workflow/start`),

  runCaseWorkflow: (caseId, signalText = '') =>
    apiClient.post(`/cases/${caseId}/workflow/run`, null, {
      params: { trigger: 'MANUAL_RUN', actor: 'User', signalText },
    }),

  completeHumanTask: (caseId, taskId) =>
    apiClient.post(`/cases/${caseId}/workflow/human-tasks/${taskId}/complete`),

  getWorkflowScheduleItems: (caseId, category) =>
    apiClient.get(`/cases/${caseId}/workflow/schedule`, {
      params: category ? { category } : {},
    }),

  saveWorkflowScheduleItem: (caseId, scheduleItemData) =>
    apiClient.post(`/cases/${caseId}/workflow/schedule`, scheduleItemData),

  updateWorkflowScheduleItemStatus: (caseId, itemId, status) =>
    apiClient.put(`/cases/${caseId}/workflow/schedule/${itemId}/status`, null, { params: { status } }),
  
  getCasesByOffice: (officeId) =>
    apiClient.get(`/cases/office/${officeId}`),
  
  updateCaseVerification: (caseId, notes) =>
    apiClient.put(`/cases/${caseId}/verify`, notes),
  
  updateCaseDraft: (caseId, content) =>
    apiClient.put(`/cases/${caseId}/draft`, content),

  generateCaseDraft: (caseId, providerId) =>
    apiClient.post(`/cases/${caseId}/draft/generate?providerId=${providerId}`),
  
  updateCaseStatus: (caseId, status) =>
    apiClient.put(`/cases/${caseId}/status?status=${status}`),
  
  assignCase: (caseId, userId) =>
    apiClient.put(`/cases/${caseId}/assign/${userId}`),
};

// Research Service
export const researchService = {
  attachResearchCase: (caseId, researchData) =>
    apiClient.post(`/cases/${caseId}/research`, researchData),
  
  searchSimilarCases: (caseType, jurisdiction, keywords) =>
    apiClient.get(`/cases/research/search`, {
      params: { caseType, jurisdiction, keywords },
    }),
  
  getResearchCasesForCase: (caseId) =>
    apiClient.get(`/cases/${caseId}/research`),

  generateResearchCases: (caseId, providerId) =>
    apiClient.post(`/cases/${caseId}/research/generate`, null, {
      params: providerId ? { providerId } : {},
    }),
  
  completeResearch: (caseId) =>
    apiClient.put(`/cases/${caseId}/research/complete`),
};

export const aiRequestPreviewService = {
  preview: (caseId, request) =>
    apiClient.post(`/cases/${caseId}/ai-request-preview`, request),
};

export const humanVerificationService = {
  getCaseTasks: (caseId) => apiClient.get(`/cases/${caseId}/human-verification/tasks`),
  requestTask: (caseId, payload) => apiClient.post(`/cases/${caseId}/human-verification/tasks`, payload),
  getMyTasks: () => apiClient.get('/human-verification/my-tasks'),
  getTask: (taskId) => apiClient.get(`/human-verification/tasks/${taskId}`),
  submitTask: (taskId, payload) => apiClient.post(`/human-verification/tasks/${taskId}/submit`, payload),
  attorneyDecision: (taskId, decision, notes) => apiClient.post(`/human-verification/tasks/${taskId}/attorney-decision`, null, { params: { decision, notes } }),
};

// Document Service
export const documentService = {
  uploadDocument: (caseId, file, documentType, notes, uploadedByUserId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    formData.append('notes', notes);
    formData.append('uploadedByUserId', uploadedByUserId);
    
    return apiClient.post(`/cases/${caseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  getDocumentsForCase: (caseId) =>
    apiClient.get(`/cases/${caseId}/documents`),
  
  getDocumentsByType: (caseId, documentType) =>
    apiClient.get(`/cases/${caseId}/documents/type/${documentType}`),

  downloadDocument: (caseId, documentId) =>
    apiClient.get(`/cases/${caseId}/documents/${documentId}/download`, {
      responseType: 'blob',
    }),

  verifyDocument: (caseId, documentId, companyId) =>
    apiClient.post(`/cases/${caseId}/documents/${documentId}/verify?companyId=${companyId}`),

  reviewDocuments: (caseId, reviewData) =>
    apiClient.post(`/cases/${caseId}/documents/review`, reviewData),
  
  deleteDocument: (caseId, documentId) =>
    apiClient.delete(`/cases/${caseId}/documents/${documentId}`),
};

export const contractService = {
  uploadContract: (caseId, file, title, contractType, counterparty, notes, uploadedByUserId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('contractType', contractType || '');
    formData.append('counterparty', counterparty || '');
    formData.append('notes', notes || '');
    formData.append('uploadedByUserId', uploadedByUserId);

    return apiClient.post(`/cases/${caseId}/contracts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getContractsForCase: (caseId) =>
    apiClient.get(`/cases/${caseId}/contracts`),

  downloadContract: (caseId, contractId) =>
    apiClient.get(`/cases/${caseId}/contracts/${contractId}/download`, {
      responseType: 'blob',
    }),

  reviewContract: (caseId, contractId, reviewData) =>
    apiClient.post(`/cases/${caseId}/contracts/${contractId}/review`, reviewData),

  deleteContract: (caseId, contractId) =>
    apiClient.delete(`/cases/${caseId}/contracts/${contractId}`),
};

export const billingService = {
  getBillingSummary: (caseId) =>
    apiClient.get(`/cases/${caseId}/billing`),

  saveBillingProfile: (caseId, profileData) =>
    apiClient.post(`/cases/${caseId}/billing/profile`, profileData),

  addTimeEntry: (caseId, timeEntryData) =>
    apiClient.post(`/cases/${caseId}/billing/time-entries`, timeEntryData),

  generateInvoice: (caseId, userId) =>
    apiClient.post(`/cases/${caseId}/billing/invoices/generate`, null, { params: { userId } }),

  sendInvoice: (caseId, invoiceId) =>
    apiClient.post(`/cases/${caseId}/billing/invoices/${invoiceId}/send`),

  createPaymentLink: (caseId, invoiceId, paymentData) =>
    apiClient.post(`/cases/${caseId}/billing/invoices/${invoiceId}/payments/link`, paymentData),

  markPaymentPaid: (caseId, invoiceId, paymentId) =>
    apiClient.post(`/cases/${caseId}/billing/invoices/${invoiceId}/payments/${paymentId}/mark-paid`),
};

export const caseAgentService = {
  createCase: (caseData) =>
    apiClient.post('/case-agent/cases', caseData),

  startSession: (sessionData) =>
    apiClient.post('/case-agent/sessions', sessionData),

  getSession: (accessToken) =>
    apiClient.get(`/case-agent/sessions/${accessToken}`),

  sendMessage: (accessToken, message) =>
    apiClient.post('/case-agent/chat', { accessToken, message }),

  previewAiRequest: (accessToken, message) =>
    apiClient.post('/case-agent/ai-request-preview', { accessToken, message }),

  uploadDocument: (accessToken, file, documentType, notes) => {
    const formData = new FormData();
    formData.append('accessToken', accessToken);
    formData.append('file', file);
    formData.append('documentType', documentType || 'Evidence');
    formData.append('notes', notes || '');

    return apiClient.post('/case-agent/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const settingsService = {
  getServiceProviders: (activeOnly = false, category) =>
    apiClient.get('/settings/service-providers', { params: { activeOnly, category } }),

  saveServiceProvider: (providerData) =>
    apiClient.post('/settings/service-providers', providerData),

  deleteServiceProvider: (providerId) =>
    apiClient.delete(`/settings/service-providers/${providerId}`),

  getLlmUsage: () =>
    apiClient.get('/settings/llm-usage'),

  previewAgenticUsageSnapshot: (params = {}) =>
    apiClient.get('/integrations/agentic-cs/usage-snapshots/preview', { params }),

  publishAgenticUsageSnapshot: (payload = {}) =>
    apiClient.post('/integrations/agentic-cs/usage-snapshots', payload),

  previewAgenticMonitoringHeartbeat: (lookbackMinutes = 15) =>
    apiClient.get('/integrations/agentic-cs/monitoring-heartbeats/preview', { params: { lookbackMinutes } }),

  publishAgenticMonitoringHeartbeat: (lookbackMinutes = 15) =>
    apiClient.post('/integrations/agentic-cs/monitoring-heartbeats', null, { params: { lookbackMinutes } }),

  getWorkflowPlaybooks: () =>
    apiClient.get('/settings/workflow-playbooks'),

  saveWorkflowPlaybook: (workflowData) =>
    apiClient.post('/settings/workflow-playbooks', workflowData),

  setWorkflowPlaybookActive: (workflowId, active) =>
    apiClient.put(`/settings/workflow-playbooks/${workflowId}/active`, null, { params: { active } }),

  getVerificationCompanies: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'DOCUMENT_VERIFICATION' },
    }),

  saveVerificationCompany: (companyData) =>
    apiClient.post('/settings/verification-companies', companyData),

  deleteVerificationCompany: (companyId) =>
    apiClient.delete(`/settings/verification-companies/${companyId}`),

  getDraftLlmProviders: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'DRAFT_LLM' },
    }),

  saveDraftLlmProvider: (providerData) =>
    apiClient.post('/settings/draft-llm-providers', providerData),

  deleteDraftLlmProvider: (providerId) =>
    apiClient.delete(`/settings/draft-llm-providers/${providerId}`),

  getResearchLlmProviders: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'RESEARCH_LLM' },
    }),

  getDocumentReviewLlmProviders: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'DOCUMENT_REVIEW_LLM' },
    }),

  getContractReviewLlmProviders: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'CONTRACT_REVIEW_LLM' },
    }),

  getPaymentProcessors: (activeOnly = false) =>
    apiClient.get('/settings/service-providers', {
      params: { activeOnly, category: 'PAYMENT_PROCESSOR' },
    }),

  saveResearchLlmProvider: (providerData) =>
    apiClient.post('/settings/research-llm-providers', providerData),

  deleteResearchLlmProvider: (providerId) =>
    apiClient.delete(`/settings/research-llm-providers/${providerId}`),
};

export default apiClient;
